import { EApplicantType, EFinancingProgram, EProvince } from '@if/core/types'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { FinancingProgramConfigs } from '@src/types/Constants'
import { ApplicantDto } from '@src/types/CreditApplicationSchema'
import { useEffect } from 'react'
import { Control, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddressComponent from './components/AddressComponent'
import ApplicantExpensesSection from './components/ApplicantExpenseComponent'
import ApplicantIncomeSection from './components/ApplicantIncomeComponent'
import ApplicantInformationParameters from './components/ApplicantInformationComponent'

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  watch: UseFormWatch<any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setValue: UseFormSetValue<any>
  errors: FieldErrors<ApplicantDto> | undefined
  applicantType: EApplicantType
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formControl: Control<any>
  editDisabled: boolean
  financingProgramId: EFinancingProgram
  hasPrequalifyBeenPressed: boolean
  isSinNeeded: boolean | null
}

const ApplicantParameters = ({
  control,
  watch,
  setValue,
  errors,
  applicantType,
  formControl,
  editDisabled,
  financingProgramId,
  hasPrequalifyBeenPressed,
  isSinNeeded,
}: Props) => {
  const { t } = useTranslation()
  const typedErrors = errors!

  useEffect(() => {
    setValue(`${applicantType}.financingProgramId`, financingProgramId)
  }, [financingProgramId, applicantType, setValue])

  const applicantProvince = watch(`${applicantType}.currentAddress.stateIso`) as EProvince
  const hasLegalName = (watch(`${applicantType}.hasLegalName`) as boolean) ?? false

  useEffect(() => {
    if (hasLegalName) setValue(`${applicantType}.lastName`, '', { shouldValidate: true })
  }, [hasLegalName, applicantType, setValue])
  const useExpenseIncomeSections =
    financingProgramId !== EFinancingProgram.Products ||
    (financingProgramId === EFinancingProgram.Products && applicantProvince === EProvince.Quebec)
  const toolTip = editDisabled ? t('applicationHeader.tooltips.lockedForModification') : ''

  return (
    <Stack spacing={2}>
      <ApplicantInformationParameters
        applicantType={applicantType}
        formControl={formControl}
        hasLegalName={hasLegalName}
        hasPrequalifyBeenPressed={hasPrequalifyBeenPressed}
        disableEdit={editDisabled}
        isSinNeeded={isSinNeeded}
      />
      <Box>
        <Divider>
          <Typography variant="h5" gutterBottom>
            {t('editCreditApplication.address.label')}
          </Typography>
        </Divider>
        <AddressComponent
          formControl={formControl}
          name={`${applicantType}.currentAddress`}
          supportedProvinces={FinancingProgramConfigs[financingProgramId].supportedProvinces}
          disableEdit={editDisabled}
        />
      </Box>

      {useExpenseIncomeSections && (
        <>
          <Divider>
            <Typography variant="h5">{t('editCreditApplication.expenses.label')}</Typography>
          </Divider>
          <ApplicantExpensesSection
            control={control}
            watch={watch}
            setValue={setValue}
            errors={typedErrors?.expenses}
            applicantType={applicantType}
            showDetailedForm={false}
            disabled={editDisabled}
            toolTip={toolTip}
          />
          <ApplicantIncomeSection
            formControl={formControl}
            applicantType={applicantType}
            disableEdit={editDisabled}
            toolTip={toolTip}
            financingProgramId={financingProgramId}
          />
        </>
      )}
    </Stack>
  )
}
export default ApplicantParameters
